import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';
import './ContentReview.css';

interface DoneIteratingButtonProps {
  cycle: ReviewCycle;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onComplete: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * Revision-loop control for cycles in 'revision_needed'.
 *
 * Two revision paths exist, and this component offers only the right one
 * (Fredrik, 2026-09-01 — the Explainer walk surfaced the wrong offer):
 *   - WEBPAGE cycles are revised IN PLACE (the CMS is the editable home),
 *     so "Done Iterating" is correct: same content, back to pending_review.
 *   - FILE cycles are revised by a NEW file — the producer submits a new
 *     version from My Submissions. Clicking Done Iterating there would send
 *     the unchanged old file back to review, so the button is replaced by a
 *     pointer to the real path.
 */
const DoneIteratingButton: React.FC<DoneIteratingButtonProps> = ({ cycle, getAuthHeaders, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show on revision_needed cycles
  if (cycle.status !== 'revision_needed') return null;

  if (cycle.contentType !== 'webpage') {
    return (
      <span data-testid="revision-upload-pointer" style={{ fontSize: 13, color: '#666' }}>
        Awaiting revised version — the producer submits the new file from{' '}
        <strong>My Submissions</strong> (version bump on the same title).
      </span>
    );
  }

  const handleClick = async () => {
    if (!window.confirm('Mark this content as done iterating? It will be sent back to reviewers.')) return;

    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/cycles/${cycle._id}/done-iterating`, {
        sharePointPath: cycle.contentItem.sharePointPath
      }, { headers });
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to mark as done iterating');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        className="review-btn review-btn-success"
        onClick={handleClick}
        disabled={loading}
        title="Content iteration is complete — send back for review"
      >
        {loading ? 'Processing...' : 'Done Iterating'}
      </button>
      {error && <span style={{ color: '#dc3545', fontSize: 12, marginLeft: 8 }}>{error}</span>}
    </div>
  );
};

export default DoneIteratingButton;
